import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Close, LogoBlack } from "../../assets/svg";
import {
  getSalesById,
  sendSales,
  updateSaleItemPrices,
} from "../../redux/sales/slice";
import { useDispatch, useSelector } from "react-redux";
import RippleLoader from "../../components/loader/RippleLoader";
import { InformationTag } from "../../components/micro/input";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import moment from "moment";
import { toast } from "react-toastify";
import { getBillingSettingList } from "../../redux/masterSetting/slice";
import ORCode from "../../assets/image/QR.png";

const ViewSales = ({
  onClose,
  selectedSaleId,
  setConfirmEmail,
  confirmEmail,
  setIsLoading,
  isLoading,
  type,
  onPriceUpdate,
}) => {
  const dispatch = useDispatch();
  const [salesData, setSalesData] = useState([]);
  const [downloadError, setDownloadError] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [sendInvoice, setSendInvoice] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPrices, setEditedPrices] = useState({});
  const [editedItems, setEditedItems] = useState([]);

  const { getSalesListData, loading } = useSelector((store) => ({
    getSalesListData: store?.salesData?.getSalesByIdStatus?.data?.data,
    loading: store?.salesData?.loading,
  }));

  useEffect(() => {
    const fetchSalesData = async () => {
      const results = await Promise.all(
        selectedSaleId.map((id) => dispatch(getSalesById({ id })))
      );
      const data = results.map((res) => res.payload.data);
      setSalesData(data);

      if (data.length > 0 && data[0]?.type === "quotation") {
        const items = [];
        data[0]?.services?.forEach((service, sIndex) =>
          service?.service_requests?.forEach((req, rIndex) => {
            // Check if service_request_item_details exists directly in the request
            if (
              req?.service_request_item_details &&
              req.service_request_item_details.length > 0
            ) {
              req.service_request_item_details.forEach((detail, dIndex) => {
                // Find corresponding service_request_item
                const item = req?.service_request_items?.[dIndex];
                items.push({
                  id: detail?.id, // Use service_request_item_details ID
                  serviceIndex: sIndex,
                  reqIndex: rIndex,
                  elIndex: dIndex,
                  price: detail?.price || item?.price || 0,
                  quantity: detail?.quantity || item?.quantity || 0,
                  title: item?.title || item?.description || "",
                });
              });
            } else {
              // Fallback to service_request_items if service_request_item_details doesn't exist
              req?.service_request_items?.forEach((el, eIndex) => {
                items.push({
                  id: el?.service_request_item_details_id || el?.id, // Try service_request_item_details_id first
                  serviceIndex: sIndex,
                  reqIndex: rIndex,
                  elIndex: eIndex,
                  price: el?.price || 0,
                  quantity: el?.quantity || 0,
                  title: el?.title || "",
                });
              });
            }
          })
        );
        setEditedItems(items);
        const map = {};
        items.forEach(
          (it) =>
            (map[`${it.serviceIndex}-${it.reqIndex}-${it.elIndex}`] =
              Number(it.price) || 0)
        );
        setEditedPrices(map);
      }
    };
    if (selectedSaleId?.length > 0) fetchSalesData();
    dispatch(getBillingSettingList());
  }, [selectedSaleId, dispatch]);

  const sendEmails = () => {
    setIsLoading(true);
    const payload = {
      ids: selectedSaleId,
      ...(type === "sendInvoiceWithReport" && { service_report: true }),
    };
    dispatch(sendSales(payload)).then((res) => {
      setIsLoading(false);
      if (res.type === "sendEmail/fulfilled") setConfirmEmail(false);
    });
  };

  const handlePriceChange = (key, val) => {
    // Remove leading zeros by converting to number, handling empty strings
    if (val === "" || val === null || val === undefined) {
      setEditedPrices((p) => ({ ...p, [key]: 0 }));
      return;
    }
    // Remove leading zeros from string before converting to number
    const cleanedVal = String(val).replace(/^0+(?=\d)/, "") || "0";
    const numValue = Number(cleanedVal);
    setEditedPrices((p) => ({ ...p, [key]: isNaN(numValue) ? 0 : numValue }));
  };

  const handleSavePrices = async () => {
    if (!salesData || salesData.length === 0 || !salesData[0]?.id) {
      toast.error("Sale data not available");
      return;
    }
    setIsLoading(true);
    try {
      const service_request_item_details = editedItems
        .map((i) => {
          const newPrice =
            editedPrices[`${i.serviceIndex}-${i.reqIndex}-${i.elIndex}`];
          // Only include items where price has changed
          if (newPrice !== undefined && newPrice !== i.price) {
            return {
              id: i.id,
              price: newPrice,
            };
          }
          return null;
        })
        .filter(Boolean); // Remove null entries

      if (service_request_item_details.length === 0) {
        toast.info("No changes to save");
        setIsEditMode(false);
        setIsLoading(false);
        return;
      }

      const updateResult = await dispatch(
        updateSaleItemPrices({
          sale_id: salesData[0].id,
          service_request_item_details,
          remarks: getSalesListData?.services?.[0]?.remarks || "",
        })
      );

      if (updateResult.type === "updateSaleItemPrices/fulfilled") {
        const results = await Promise.all(
          selectedSaleId.map((id) => dispatch(getSalesById({ id })))
        );
        setSalesData(results.map((r) => r.payload.data));
        setIsEditMode(false);

        if (onPriceUpdate) {
          onPriceUpdate();
        }
      }
    } catch {
      toast.error("Failed to update pricing");
    } finally {
      setIsLoading(false);
    }
  };

  const calcTotals = (d) => {
    let sub = 0;
    d?.services?.forEach((s, si) =>
      s?.service_requests?.forEach((r, ri) =>
        r?.service_request_items?.forEach((e, ei) => {
          const k = `${si}-${ri}-${ei}`;
          const up =
            isEditMode && editedPrices[k] !== undefined
              ? editedPrices[k]
              : e?.price || 0;
          sub += up * (e?.quantity || 0);
        })
      )
    );
    const gst = d?.gst || 0;
    const tax = (sub * gst) / 100;
    return { sub, tax, total: sub + tax };
  };

  const handlePDF = async () => {
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth();
      for (let i = 0; i < salesData.length; i++) {
        const node = document.getElementById(`salePage-${i}`);
        const canvas = await html2canvas(node, { scale: 2 });
        const img = canvas.toDataURL("image/png");
        const h = (canvas.height * w) / canvas.width;
        if (i > 0) pdf.addPage();
        pdf.addImage(img, "PNG", 0, 0, w, h);
      }
      pdf.save("invoice.pdf");
      toast.success("Downloaded successfully!");
    } catch {
      setDownloadError(true);
      toast.error("PDF Download Error");
    }
  };

  const handleSendClick = () => {
    if (
      type === "sendInvoiceWithReport" &&
      !getSalesListData?.service?.service_reports
    ) {
      setSendInvoice(true);
    } else {
      setShowConfirmationModal(true);
    }
  };

  const handleSendInvoiceConfirm = () => {
    sendEmails();
    setSendInvoice(false);
  };

  const renderInvoice = (d, i) => {
    const totals = calcTotals(d);
    const canEdit =
      d?.type === "quotation" &&
      (d?.status === "in system" || d?.status === "autocount");

    return (
      <div
        id={`salePage-${i}`}
        key={i}
        className="mx-auto bg-white"
        style={{
          width: "210mm",
          padding: "28px 32px",
          fontFamily: "Arial, sans-serif",
          color: "#000",
        }}
      >
        {/* Header Section - Logo left, Company info immediately to the right */}
        <div className="flex items-start mb-3">
          <div className="mr-6 flex-shrink-0">
            <LogoBlack />
          </div>
          <div style={{ lineHeight: "1.4" }}>
            <p className="text-[14px] font-Proxima text-gray-600 mb-1">
              UEN: 201402444D
            </p>
            <p className="text-2xl font-Montserrat font-semibold text-primaryBlack mb-1 border-b-2 border-themeBtn max-w-72">
              FBK GROUP PTE LTD
            </p>
            <p className="text-xl font-Montserrat font-semibold text-primaryBlack">
              fnbkitchen.com.sg
            </p>
            <div className="max-w-full overflow-hidden text-nowrap">
              <p className="text-sm font-Proxima text-primaryBlack border-b-2 border-themeBtn">
                3 Ubi Ave 3 #03-04 Crocodile House S408857{" "}
                <span className="text-themeBtn font-bold text-2xl">•</span>{" "}
                <span className="font-bold">E:</span> info@fbk.com.sg{" "}
                <span className="text-themeBtn mx-1 text-2xl">•</span>{" "}
                <span className="font-bold">T:</span> 64433824{" "}
                <span className="text-themeBtn mx-1 text-2xl">•</span>{" "}
                <span className=" font-bold">F:</span> 64433825
              </p>
            </div>
          </div>
        </div>

        {/* Document Title - Centered */}
        <div className="text-center mb-6">
          <h1
            className="text-3xl font-Montserrat font-bold text-primaryBlack"
            style={{ textDecorationThickness: "2px" }}
          >
            {d?.type?.toUpperCase() || "TAX INVOICE"}
          </h1>
          <p className="text-sm font-Proxima text-primaryBlack">
            GST Reg No: 201402444D
          </p>
        </div>

        {/* Customer and Invoice Details */}
        <div className="flex justify-between mb-6">
          <div className="w-1/2 pr-6">
            <p className="font-Montserrat font-bold text-base text-primaryBlack mb-2 uppercase">
              {d?.company?.name?.toUpperCase() || "-"}
            </p>
            <p className="text-sm font-Proxima text-primaryBlack leading-tight">
              {d?.company?.address || "-"}
            </p>
            {d?.company?.postal_code && (
              <p className="text-sm font-Proxima text-primaryBlack leading-tight">
                {d?.company?.postal_code}
              </p>
            )}
            <div className="mt-4 space-y-0.5 text-sm font-Proxima text-primaryBlack">
              <p>Invoice No. : {d?.unique_id || "-"}</p>
              <p>Ref No. : {d?.ref_no || "-"}</p>
            </div>
          </div>
          <div className="w-1/2 pl-6">
            <div className="space-y-0.5 text-sm font-Proxima text-primaryBlack text-right">
              <p>Attn : {d?.company?.contact_person || "Mr/Ms"}</p>
              <p>Tel : {d?.company?.phone || "-"}</p>
              <p>Email : {d?.company?.email || "-"}</p>
              <p>
                Date :{" "}
                {d?.created_at
                  ? moment(d.created_at).format("DD/MM/YYYY")
                  : moment().format("DD/MM/YYYY")}
              </p>
              <p>Terms : {d?.payment_terms || "C.O.D."}</p>
            </div>
          </div>
        </div>

        {/* Service Title - Centered above table */}
        <div className="mb-2">
          <p className="text-lg font-Montserrat font-bold text-primaryBlack text-center">
            {d?.services?.[0]?.service_requests?.[0]?.service_request_items?.[0]
              ?.title || ""}
          </p>
        </div>

        {/* Items Table */}
        <div className="mb-6">
          <table className="w-full border-b border-gray-400">
            <thead className="border-b border-gray-400 border-t">
              <tr style={{ backgroundColor: "rgba(183, 44, 34, 0.04)" }}>
                <th className="px-3 py-2 text-left font-Proxima text-sm font-normal text-primaryBlack">
                  S/N
                </th>
                <th className="px-3 py-2 text-left font-Proxima text-sm font-normal text-primaryBlack">
                  Qty
                </th>
                <th className="px-3 py-2 text-left font-Proxima text-sm font-normal text-primaryBlack">
                  UNIT
                </th>
                <th className="px-3 py-2 text-left font-Proxima text-sm font-normal text-primaryBlack">
                  Description
                </th>
                <th className="px-3 py-2 text-right font-Proxima text-sm font-normal text-primaryBlack">
                  S$ U/Price
                </th>
                <th className="px-3 py-2 text-right font-Proxima text-sm font-normal text-primaryBlack">
                  S$ Total
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let globalIndex = 0;
                return d?.services?.map((s, si) =>
                  s?.service_requests?.map((r, ri) =>
                    r?.service_request_items?.map((e, ei) => {
                      const key = `${si}-${ri}-${ei}`;
                      const rawPrice =
                        isEditMode && editedPrices[key] !== undefined
                          ? editedPrices[key]
                          : e?.price;
                      // Always convert to number to remove leading zeros
                      const up = rawPrice ? Number(rawPrice) : 0;
                      const qty = Number(e?.quantity) || 0;
                      const total = up * qty;
                      const serialNumber = ++globalIndex;
                      const priceValue = up;
                      return (
                        <tr
                          key={key}
                          className="font-Proxima text-sm text-primaryBlack"
                        >
                          <td className="px-3 py-2">{serialNumber}.</td>
                          <td className="px-3 py-2">{qty}</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2">
                            {e?.title || e?.description || "-"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {canEdit && isEditMode ? (
                              <input
                                type="text"
                                inputMode="decimal"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                value={
                                  priceValue === 0
                                    ? ""
                                    : String(priceValue).replace(
                                        /^0+(?=\d)/,
                                        ""
                                      )
                                }
                                onChange={(ev) => {
                                  let inputVal = ev.target.value;
                                  // Remove any non-numeric characters except decimal point
                                  inputVal = inputVal.replace(/[^\d.]/g, "");
                                  // Remove leading zeros
                                  inputVal = inputVal.replace(/^0+(?=\d)/, "");
                                  // Handle empty string
                                  if (inputVal === "" || inputVal === ".") {
                                    handlePriceChange(key, "0");
                                  } else {
                                    handlePriceChange(key, inputVal);
                                  }
                                }}
                                onBlur={(ev) => {
                                  // Ensure value is a clean number on blur
                                  const numVal =
                                    parseFloat(ev.target.value) || 0;
                                  setEditedPrices((p) => ({
                                    ...p,
                                    [key]: numVal,
                                  }));
                                }}
                              />
                            ) : (
                              `${Number(priceValue).toFixed(2)}`
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {Number(total).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      );
                    })
                  )
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* Bank Details, QR Code, and Financial Summary */}
        <div className="mb-6 pb-4">
          <div className="flex flex-row justify-between items-start">
            {/* Bank Details - Left */}
            <div className="flex-1 text-sm font-Proxima text-primaryBlack font-normal">
              <p>
                NAME OF ACCOUNT :{" "}
                <span className="font-extrabold">FBK GROUP PTE LTD</span>
              </p>
              <p>
                ACCOUNT NO. :{" "}
                <span className="font-extrabold">615-884798-001</span>
              </p>
              <p>
                SWIFT CODE : <span className="font-extrabold">OCBCSGSG</span>
              </p>
              <p>
                BANK INFORMATION :{" "}
                <span className="font-extrabold">
                  OCBC LIMITED SINGAPORE 502 JURONG WEST AVE 1
                </span>
                #01-821 SINGAPORE 640502
              </p>
              <p>
                PAYNOW NO : <span className="font-extrabold">201402444D</span>
              </p>
            </div>
            {/* QR Code - Center */}
            <div className="flex flex-col items-center mx-4">
              <div className="w-32 h-32 border-2 border-gray-400 flex items-center justify-center bg-white flex-shrink-0 mb-2">
                <img src={ORCode} className="w-full h-full" />
              </div>
              <div
                className="bg-red text-white px-4 py-2 text-sm font-Proxima font-bold text-center"
                style={{ minWidth: "130px" }}
              >
                SCAN TO PAY
              </div>
            </div>
            {/* Financial Summary - Right */}
            <div className="flex-1 text-right text-sm font-Proxima text-primaryBlack">
              <p className="font-Proxima">
                Sub Total(Excluding Tax) :{" "}
                <span className="font-Proxima font-bold">
                  {Number(totals.sub).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </p>
              <p className="font-Proxima">
                Tax @ {d?.gst || 9}% on{" "}
                {Number(totals.sub).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                :{" "}
                <span className="font-Proxima font-bold">
                  {Number(totals.tax).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </p>
              <p className="font-Proxima">
                Total (Inclusive of Tax) :{" "}
                <span className="font-Proxima font-bold">
                  {Number(totals.total).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        {/* <div className="flex justify-between items-start mb-6">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center border-b-2 border-black pb-2 ">
              <div className="h-16 w-48 mb-2 flex justify-center items-center text-center">
                Jing Jing
              </div>
              <div className="w-24 h-24 border-2 border-black rounded-full flex items-center justify-center flex-shrink-0 relative overflow-visible">
                <div className="text-center text-[7px] font-Proxima px-2 z-10">
                  <p className="text-[6px] leading-tight">Reg. No.</p>
                  <p className="text-[6px] font-bold leading-tight">
                    201402444D
                  </p>
                  <p className="text-[8px] leading-tight mt-0.5">★</p>
                  <p className="text-[8px] font-bold leading-tight mt-0.5">
                    FBK
                  </p>
                </div>
              </div>
            </div>
            <p className="text-sm font-Proxima font-bold text-primaryBlack uppercase">
              CHALK FARM PTE LTD
            </p>
          </div>
          <div className="flex flex-col items-center">
            <div className="border-b-2 border-black w-48 mb-2 h-24 flex justify-center items-center text-center">
              Jing Jing
            </div>
            <p className="text-sm font-Proxima font-bold text-primaryBlack uppercase">
              JING JING
            </p>
          </div>
        </div> */}

        {/* Terms and Conditions */}
        <div className=" pt-4">
          <div className="grid grid-cols-2 gap-6 text-xs font-Proxima text-primaryBlack">
            <div>
              <p className="mb-2 text-sm">
                <span className="font-bold">TERMS AND CONDITIONS</span> (Subject
                to change without prior notice)
              </p>
              <ul className="list-disc list-inside space-y-0.5 pl-3 text-xs leading-relaxed">
                <li>
                  The above equipment sold are non-refundable, non-transferable
                  and non-exchangeable
                </li>
                <li>
                  We reserve the right to repossess the above equipment if
                  payment is not made or not made in full within 30days after
                  delivery and all deposit will be forfeited
                </li>
                <li>
                  Any orders that are not collected or delivered after 14days
                  payment is not made or not made in full within 30days after
                  delivery and all deposit will be forfeited
                </li>
                <li>
                  All deposit will be forfeited for any cancellation of custom
                  made or indented items
                </li>
                <li>
                  Interest of 2% per month will be charged to overdue invoices
                  unless otherwise stated in the payment terms
                </li>
                <li>
                  Warranty repair is only during working hours (Mon-Fri 9am-6pm)
                </li>
                <li>
                  Surcharge applies for warranty repair outside of working hours
                </li>
              </ul>
            </div>
            <div>
              <ul className="list-disc list-inside space-y-0.5 pl-3 text-xs leading-relaxed">
                <li>
                  Warranty period: 12 months for new equipment and 3 months for
                  used equipment unless otherwise stated
                </li>
                <li>
                  Warranty does not cover wear and tear parts of the above
                  equipment
                </li>
                <li>
                  12 months warranty will only commence from the date of
                  delivery upon the receipt of full payment unless otherwise
                  stated
                </li>
                <li>
                  Warranty will be void if there is a change of owner or
                  location to the above equipment unless the moving is
                  facilitated by us
                </li>
                <li>
                  Warranty will be void if equipment is not use/operating in its
                  original condition
                </li>
                <li>
                  We will not be resposible for any loss incurred due to the
                  breakdown of the above equipment
                </li>
                <li>
                  No warranty for repair and replaced parts unless otherwise
                  stated
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="model" onClick={onClose}>
      <div
        className="bg-white rounded-lg max-w-6xl py-8 px-6 mx-auto relative max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-end mb-2">
          <button onClick={onClose}>
            <Close />
          </button>
        </div>

        {loading ? (
          <div className="w-full">
            <RippleLoader />
          </div>
        ) : (
          <>
            {salesData?.map((d, idx) => (
              <div key={idx}>{renderInvoice(d, idx)}</div>
            ))}

            <div className="flex justify-end gap-2 pt-2">
              {salesData?.some(
                (d) =>
                  d?.type === "quotation" &&
                  (d?.status === "in system" || d?.status === "autocount")
              ) && (
                <>
                  {!isEditMode ? (
                    <button
                      onClick={() => setIsEditMode(true)}
                      type="button"
                      className="text-themeBtn flex items-center uppercase font-Proxima tex-[15px] justify-center px-6 py-1.5 rounded border border-themeBtn"
                    >
                      Edit Pricing
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setIsEditMode(false);
                          const priceMap = {};
                          editedItems.forEach((item) => {
                            priceMap[
                              `${item.serviceIndex}-${item.reqIndex}-${item.elIndex}`
                            ] = item.price;
                          });
                          setEditedPrices(priceMap);
                        }}
                        type="button"
                        className="text-themeBtn flex items-center uppercase font-Proxima tex-[15px] justify-center px-6 py-1.5 rounded border border-themeBtn"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSavePrices}
                        type="button"
                        className="text-white flex items-center uppercase font-Proxima text-[15px] justify-center px-6 py-1.5 rounded border bg-themeBtn"
                        disabled={isLoading}
                      >
                        {isLoading ? "Saving..." : "Save Pricing"}
                      </button>
                    </>
                  )}
                </>
              )}
              <button
                onClick={confirmEmail ? handleSendClick : handlePDF}
                type="button"
                className="text-themeBtn flex items-center uppercase font-Proxima tex-[15px] justify-center px-6 py-1.5 rounded border border-themeBtn"
              >
                <span>{confirmEmail ? "Send" : "Download"}</span>
              </button>
            </div>
          </>
        )}
      </div>

      {downloadError && (
        <div className="model">
          <div
            className="modal-content bg-white rounded-lg w-md py-2 px-6 mx-auto relative overflow-auto min-h-40 items-center flex"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDownloadError(false)}
              className="absolute top-4 right-4 flex justify-end items-start"
            >
              <Close />
            </button>
            <div className="flex flex-col justify-center items-center mt-4">
              <InformationTag type="error" size="40" />
              <h2 className="font-Montserrat text-center font-normal text-2xl mt-4">
                Unable to Download
              </h2>
              <p className="text-base font-Proxima mb-3 text-center font-normal">
                Please try again later.
              </p>
            </div>
          </div>
        </div>
      )}
      {showConfirmationModal && (
        <ConfirmationModel
          confirmText={
            type === "sendQuotation"
              ? "Send Quotation"
              : type === "sendInvoice"
              ? "Send Invoice"
              : type === "sendInvoiceWithReport"
              ? "Send Invoice With Report"
              : type === "sendReceipt"
              ? "Send Receipt"
              : "Unknown Action"
          }
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={() => sendEmails()}
          isLoading={isLoading}
        />
      )}
      {sendInvoice && (
        <div className="model">
          <div
            className="bg-white flex flex-col items-center justify-center drop-shadow-lg rounded-lg w-full max-w-md min-h-40 py-8 px-6 mx-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-Montserrat mb-3 font-normal">
              Confirm Send Invoice With <br />
              Missing Service Reports?
            </h2>
            <p className="text-sm font-Proxima mb-3 text-start font-normal">
              The following service reports For the following invoice No. are
              missing :
            </p>
            <ul className="text-sm font-Proxima mb-3 font-normal text-start">
              {salesData?.map((data, index) => (
                <div key={index}>
                  <li>{data?.unique_id}</li>
                </div>
              ))}
            </ul>
            <div className="flex gap-4 mt-4">
              <button
                onClick={() => setSendInvoice(false)}
                type="button"
                className="text-themeBtn flex items-center uppercase font-Proxima text-[15px] justify-center px-6 py-1.5 rounded border border-themeBtn"
              >
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSendInvoiceConfirm}
                type="button"
                className="flex items-center uppercase font-Proxima text-[15px] justify-center px-6 py-1.5 rounded border bg-themeBtn text-white"
              >
                <span>Confirm</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewSales;
